import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import callsService from '../../../services/appointments/calls';
import { downloadZipFile, formatDateForFilename, metricsToCSV, tableToCSV } from '../../../utils/exportUtils';
import { CustomTable } from '../../common/CustomTable';
import { AgentsTab } from './AgentsTab';
import { OverviewTab } from './OverviewTab';
import { SmsAgentsTab } from './SmsAgentsTab';
import { SmsOverviewTab } from './SmsOverviewTab';
import { SmsTeamsTab } from './SmsTeamsTab';
import { TeamsTab } from './TeamsTab';
import { VoicemailsAgentsTab } from './VoicemailsAgentsTab';
import { VoicemailsOverviewTab } from './VoicemailsOverviewTab';
import { VoicemailsTeamsTab } from './VoicemailsTeamsTab';

type Props = {
  breadcrumbs: ReactNode;
};

type DatePreset = 'today' | 'last7days' | 'last30days' | 'custom';

type MetricsType = 'calls' | 'voicemails' | 'sms';

type SubTab = 'overview' | 'agents' | 'teams';

const MAX_TIME_WINDOW_MONTHS = 3; // Maximum duration of custom date range (not how far back you can go)

export const MetricsCenter = ({ breadcrumbs }: Props) => {
  const [metricsType, setMetricsType] = useState<MetricsType>('calls');
  const [callsSubTab, setCallsSubTab] = useState<SubTab>('overview');
  const [voicemailsSubTab, setVoicemailsSubTab] = useState<SubTab>('overview');
  const [smsSubTab, setSmsSubTab] = useState<SubTab>('overview');
  const [datePreset, setDatePreset] = useState<DatePreset>('last7days');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<any>(null);
  const [comparison, setComparison] = useState<any>(null);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [currentStartDate, setCurrentStartDate] = useState<string>('');
  const [currentEndDate, setCurrentEndDate] = useState<string>('');
  const [previousStartDate, setPreviousStartDate] = useState<string>('');
  const [previousEndDate, setPreviousEndDate] = useState<string>('');

  const notify = useNotify();

  const validateCustomDates = useCallback(() => {
    if (!customStartDate || !customEndDate) {
      notify('Please select both start and end dates', { type: 'warning' });
      return false;
    }

    const start = new Date(customStartDate);
    const end = new Date(customEndDate);
    const now = new Date();

    // Check: start date must be before end date
    if (start > end) {
      notify('Start date must be before end date', { type: 'error' });
      return false;
    }

    // Check: end date cannot be in the future
    if (end > now) {
      notify('End date cannot be in the future', { type: 'error' });
      return false;
    }

    // Check: date range duration cannot exceed MAX_TIME_WINDOW_MONTHS
    const maxEndDate = new Date(start);
    maxEndDate.setMonth(maxEndDate.getMonth() + MAX_TIME_WINDOW_MONTHS);

    if (end > maxEndDate) {
      notify(
        `Date range cannot exceed ${MAX_TIME_WINDOW_MONTHS} months. Please select a shorter time period.`,
        { type: 'error' },
      );
      return false;
    }

    return true;
  }, [customStartDate, customEndDate, notify]);

  const getDateRange = useCallback(
    (preset: DatePreset) => {
      const now = new Date();
      const endDate = now.toISOString();
      let startDate: string;

      switch (preset) {
        case 'today':
          const today = new Date(now);
          today.setHours(0, 0, 0, 0);
          startDate = today.toISOString();
          break;
        case 'last7days':
          const last7 = new Date(now);
          last7.setDate(last7.getDate() - 7);
          startDate = last7.toISOString();
          break;
        case 'last30days':
          const last30 = new Date(now);
          last30.setDate(last30.getDate() - 30);
          startDate = last30.toISOString();
          break;
        case 'custom':
          if (!validateCustomDates()) {
            return null;
          }
          return {
            startDate: new Date(customStartDate).toISOString(),
            endDate: new Date(customEndDate + 'T23:59:59').toISOString(),
          };
        default:
          startDate = endDate;
      }

      return { startDate, endDate };
    },
    [customStartDate, customEndDate, validateCustomDates],
  );

  const getPreviousPeriod = useCallback(
    (preset: DatePreset, currentStart: string, currentEnd: string) => {
      let compareStart: Date;
      let compareEnd: Date;

      if (preset === 'custom') {
        const start = new Date(currentStart);
        const end = new Date(currentEnd);
        const duration = end.getTime() - start.getTime();

        compareEnd = new Date(start);
        compareEnd.setMilliseconds(-1);
        compareStart = new Date(compareEnd.getTime() - duration);
      } else {
        const now = new Date();
        switch (preset) {
          case 'today':
            compareEnd = new Date(now);
            compareEnd.setDate(compareEnd.getDate() - 1);
            compareEnd.setHours(23, 59, 59, 999);
            compareStart = new Date(compareEnd);
            compareStart.setHours(0, 0, 0, 0);
            break;
          case 'last7days':
            compareEnd = new Date(now);
            compareEnd.setDate(compareEnd.getDate() - 7);
            compareStart = new Date(compareEnd);
            compareStart.setDate(compareStart.getDate() - 7);
            break;
          case 'last30days':
            compareEnd = new Date(now);
            compareEnd.setDate(compareEnd.getDate() - 30);
            compareStart = new Date(compareEnd);
            compareStart.setDate(compareStart.getDate() - 30);
            break;
          default:
            compareStart = now;
            compareEnd = now;
        }
      }

      return {
        compareStartDate: compareStart.toISOString(),
        compareEndDate: compareEnd.toISOString(),
      };
    },
    [],
  );

  // Helper function to export a subsection
  const exportSubsection = async (subsection: 'overview' | 'agents' | 'teams') => {
    if (!metrics || !currentStartDate || !currentEndDate) return;

    const files: Array<{ filename: string; content: string }> = [];
    const dateStr = formatDateForFilename();
    const sectionName = metricsType.charAt(0).toUpperCase() + metricsType.slice(1);
    const subsectionName = subsection.charAt(0).toUpperCase() + subsection.slice(1);

    try {
      if (subsection === 'overview') {
        // Export overview tables and metrics based on metricsType
        if (metricsType === 'calls') {
          // Key metrics
          const keyMetrics = [
            { label: 'Total Calls', value: metrics.totalCalls, previousValue: comparison?.totalCalls },
            { label: 'Inbound', value: metrics.inboundCalls, previousValue: comparison?.inboundCalls },
            { label: 'Outbound', value: metrics.outboundCalls, previousValue: comparison?.outboundCalls },
            { label: 'Answered', value: metrics.answeredCalls, previousValue: comparison?.answeredCalls },
            { label: 'Missed', value: metrics.missedCalls, previousValue: comparison?.missedCalls },
            { label: 'Average Duration (seconds)', value: metrics.averageDuration, previousValue: comparison?.averageDuration },
            { label: 'Average Wait Time (seconds)', value: metrics.averageWaitTime, previousValue: comparison?.averageWaitTime },
            { label: 'Average Answer Time (seconds)', value: metrics.averageAnswerTime, previousValue: comparison?.averageAnswerTime },
          ];
          files.push({
            filename: `${dateStr}-calls-overview-key-metrics.csv`,
            content: metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });

          // Inbound metrics
          if (metrics.inboundDetails) {
            const inboundMetrics = [
              { label: 'Answered', value: metrics.inboundDetails.answered, previousValue: comparison?.inboundDetails?.answered },
              { label: 'Missed', value: metrics.inboundDetails.missed, previousValue: comparison?.inboundDetails?.missed },
              { label: 'Abandoned', value: metrics.inboundDetails.abandoned, previousValue: comparison?.inboundDetails?.abandoned },
              { label: 'Answer Rate (%)', value: metrics.inboundDetails.answerRate?.toFixed(1) ?? '0.0', previousValue: comparison?.inboundDetails?.answerRate?.toFixed(1) },
            ];
            files.push({
              filename: `${dateStr}-calls-overview-inbound.csv`,
              content: metricsToCSV(inboundMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
            });
          }

          // Outbound metrics
          if (metrics.outboundDetails) {
            const outboundMetrics = [
              { label: 'Completed', value: metrics.outboundDetails.completed, previousValue: comparison?.outboundDetails?.completed },
              { label: 'No Answer', value: metrics.outboundDetails.noAnswer, previousValue: comparison?.outboundDetails?.noAnswer },
              { label: 'Failed', value: metrics.outboundDetails.failed, previousValue: comparison?.outboundDetails?.failed },
              { label: 'Success Rate (%)', value: metrics.outboundDetails.successRate?.toFixed(1) ?? '0.0', previousValue: comparison?.outboundDetails?.successRate?.toFixed(1) },
            ];
            files.push({
              filename: `${dateStr}-calls-overview-outbound.csv`,
              content: metricsToCSV(outboundMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
            });
          }

          // Quality metrics
          if (metrics.ratingMetrics) {
            const qualityMetrics = [
              { label: 'Evaluated Calls', value: metrics.ratingMetrics.evaluatedCalls, previousValue: comparison?.ratingMetrics?.evaluatedCalls },
              { label: 'Unevaluated Calls', value: metrics.ratingMetrics.unevaluatedCalls, previousValue: comparison?.ratingMetrics?.unevaluatedCalls },
              { label: 'Evaluation Rate (%)', value: metrics.ratingMetrics.evaluationRate?.toFixed(1) ?? '0.0', previousValue: comparison?.ratingMetrics?.evaluationRate?.toFixed(1) },
              { label: 'Average Rating', value: metrics.ratingMetrics.averageRating?.toFixed(2) ?? '0.00', previousValue: comparison?.ratingMetrics?.averageRating?.toFixed(2) },
              { label: 'Positive Rating Rate (%)', value: metrics.ratingMetrics.positiveRatingRate?.toFixed(1) ?? '0.0', previousValue: comparison?.ratingMetrics?.positiveRatingRate?.toFixed(1) },
            ];
            files.push({
              filename: `${dateStr}-calls-overview-quality-metrics.csv`,
              content: metricsToCSV(qualityMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
            });
          }

          // Tables
          if (metrics.callsByStatus && metrics.callsByStatus.length > 0) {
            files.push({ filename: `${dateStr}-calls-overview-by-status.csv`, content: tableToCSV(
              [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }],
              metrics.callsByStatus.map((s: any) => ({ status: s.status, count: s.count }))
            )});
          }

          if (metrics.callsByPurpose && metrics.callsByPurpose.length > 0) {
            files.push({ filename: `${dateStr}-calls-overview-by-purpose.csv`, content: tableToCSV(
              [{ key: 'purpose', label: 'Purpose' }, { key: 'totalCalls', label: 'Total' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
              metrics.callsByPurpose.map((p: any) => ({ purpose: p.purpose || 'Unknown', totalCalls: p.totalCalls, inbound: p.inboundCalls, outbound: p.outboundCalls }))
            )});
          }
        } else if (metricsType === 'voicemails') {
          // Key metrics
          const keyMetrics = [
            { label: 'Total Voicemails', value: metrics.totalVoicemails, previousValue: comparison?.totalVoicemails },
            { label: 'Inbound', value: metrics.inboundVoicemails, previousValue: comparison?.inboundVoicemails },
            { label: 'Outbound', value: metrics.outboundVoicemails, previousValue: comparison?.outboundVoicemails },
            { label: 'Avg Duration (seconds)', value: metrics.averageDuration, previousValue: comparison?.averageDuration },
          ];
          files.push({
            filename: `${dateStr}-voicemails-overview-key-metrics.csv`,
            content: metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });

          // Tables
          if (metrics.voicemailsByStatus && metrics.voicemailsByStatus.length > 0) {
            files.push({ filename: `${dateStr}-voicemails-overview-by-status.csv`, content: tableToCSV(
              [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }],
              metrics.voicemailsByStatus.map((s: any) => ({ status: s.status, count: s.count }))
            )});
          }
        } else if (metricsType === 'sms') {
          // Key metrics
          const keyMetrics = [
            { label: 'Total Messages', value: metrics.totalMessages, previousValue: comparison?.totalMessages },
            { label: 'Inbound', value: metrics.inboundMessages, previousValue: comparison?.inboundMessages },
            { label: 'Outbound', value: metrics.outboundMessages, previousValue: comparison?.outboundMessages },
            { label: 'Avg Length (chars)', value: metrics.averageLength, previousValue: comparison?.averageLength },
            { label: 'Max Length (chars)', value: metrics.maxLength, previousValue: comparison?.maxLength },
            { label: 'Min Length (chars)', value: metrics.minLength, previousValue: comparison?.minLength },
          ];
          files.push({
            filename: `${dateStr}-sms-overview-key-metrics.csv`,
            content: metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });

          // Tables
          if (metrics.messagesByStatus && metrics.messagesByStatus.length > 0) {
            files.push({ filename: `${dateStr}-sms-overview-by-status.csv`, content: tableToCSV(
              [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
              metrics.messagesByStatus.map((s: any) => ({ status: s.status, count: s.count, inbound: s.inboundCount, outbound: s.outboundCount }))
            )});
          }

          if (metrics.messagesByTrackingNumber && metrics.messagesByTrackingNumber.length > 0) {
            files.push({ filename: `${dateStr}-sms-overview-by-tracking-number.csv`, content: tableToCSV(
              [{ key: 'label', label: 'Number Label' }, { key: 'count', label: 'Count' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
              metrics.messagesByTrackingNumber.slice(0, 10).map((tn: any) => ({ label: tn.label, count: tn.count, inbound: tn.inboundCount, outbound: tn.outboundCount }))
            )});
          }

          if (metrics.messagesByCategory && metrics.messagesByCategory.length > 0) {
            files.push({ filename: `${dateStr}-sms-overview-by-category.csv`, content: tableToCSV(
              [{ key: 'category', label: 'Category' }, { key: 'count', label: 'Count' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
              metrics.messagesByCategory.map((c: any) => ({ category: c.category, count: c.count, inbound: c.inboundCount, outbound: c.outboundCount }))
            )});
          }
        }
      } else if (subsection === 'agents') {
        // Export agents data
        if (metricsType === 'calls' && metrics.callsByAgent && metrics.callsByAgent.length > 0) {
          files.push({ filename: `${dateStr}-calls-agents.csv`, content: tableToCSV(
            [{ key: 'agentName', label: 'Agent' }, { key: 'total', label: 'Total Calls' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
            metrics.callsByAgent.map((a: any) => ({ agentName: a.agentName || 'Unknown', total: a.count, inbound: a.inboundCount, outbound: a.outboundCount }))
          )});
        } else if (metricsType === 'voicemails' && metrics.voicemailsByAgent && metrics.voicemailsByAgent.length > 0) {
          files.push({ filename: `${dateStr}-voicemails-agents.csv`, content: tableToCSV(
            [{ key: 'agentName', label: 'Agent' }, { key: 'total', label: 'Total' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
            metrics.voicemailsByAgent.map((a: any) => ({ agentName: a.agentName || 'Unknown', total: a.count, inbound: a.inboundCount, outbound: a.outboundCount }))
          )});
        } else if (metricsType === 'sms' && metrics.messagesByAgent && metrics.messagesByAgent.length > 0) {
          files.push({ filename: `${dateStr}-sms-agents.csv`, content: tableToCSV(
            [{ key: 'agentName', label: 'Agent' }, { key: 'sent', label: 'Sent' }, { key: 'received', label: 'Received' }, { key: 'total', label: 'Total' }],
            metrics.messagesByAgent.map((a: any) => ({
              agentName: a.agentName || a.agent_name || 'Unknown',
              sent: a.sent_messages || a.sentMessages || a.outbound || 0,
              received: a.received_messages || a.receivedMessages || a.inbound || 0,
              total: a.total_messages || a.totalMessages || a.count || 0
            }))
          )});
        }
      } else if (subsection === 'teams') {
        // Export teams data
        if (metricsType === 'calls' && metrics.callsByTeam && metrics.callsByTeam.length > 0) {
          // Export each team as separate file
          metrics.callsByTeam.forEach((team: any) => {
            const comparisonTeam = comparison?.callsByTeam?.find((t: any) => t.teamId === team.teamId);
            const teamMetrics = [
              { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
              { label: 'Total Calls', value: team.totalCalls, previousValue: comparisonTeam?.totalCalls },
              { label: 'Inbound', value: team.inboundCalls, previousValue: comparisonTeam?.inboundCalls },
              { label: 'Outbound', value: team.outboundCalls, previousValue: comparisonTeam?.outboundCalls },
              { label: 'Answered', value: team.answeredCalls, previousValue: comparisonTeam?.answeredCalls },
              { label: 'Average Duration (seconds)', value: team.averageDuration, previousValue: comparisonTeam?.averageDuration },
            ];
            files.push({
              filename: `${dateStr}-calls-team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}.csv`,
              content: metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
            });
          });
        } else if (metricsType === 'voicemails' && metrics.voicemailsByTeam && metrics.voicemailsByTeam.length > 0) {
          metrics.voicemailsByTeam.forEach((team: any) => {
            const comparisonTeam = comparison?.voicemailsByTeam?.find((t: any) => t.teamId === team.teamId);
            const teamMetrics = [
              { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
              { label: 'Total Voicemails', value: team.totalVoicemails, previousValue: comparisonTeam?.totalVoicemails },
              { label: 'Inbound', value: team.inboundVoicemails, previousValue: comparisonTeam?.inboundVoicemails },
              { label: 'Outbound', value: team.outboundVoicemails, previousValue: comparisonTeam?.outboundVoicemails },
            ];
            files.push({
              filename: `${dateStr}-voicemails-team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}.csv`,
              content: metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
            });
          });
        } else if (metricsType === 'sms' && metrics.messagesByTeam && metrics.messagesByTeam.length > 0) {
          metrics.messagesByTeam.forEach((team: any) => {
            const comparisonTeam = comparison?.messagesByTeam?.find((t: any) => t.teamId === team.teamId);
            const teamMetrics = [
              { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
              { label: 'Total Messages', value: team.totalMessages, previousValue: comparisonTeam?.totalMessages },
              { label: 'Inbound', value: team.inboundMessages, previousValue: comparisonTeam?.inboundMessages },
              { label: 'Outbound', value: team.outboundMessages, previousValue: comparisonTeam?.outboundMessages },
            ];
            files.push({
              filename: `${dateStr}-sms-team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}.csv`,
              content: metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
            });
          });
        }
      }

      if (files.length > 0) {
        await downloadZipFile(files, `${dateStr}-${sectionName}-${subsectionName}.zip`);
        notify(`Exported ${subsectionName} section (${files.length} file(s))`, { type: 'success' });
      } else {
        notify('No data available to export for this subsection', { type: 'warning' });
      }
    } catch (error) {
      console.error('Export error:', error);
      notify('Error exporting subsection', { type: 'error' });
    }
  };

  // Global export function - exports ALL subsections (Overview + Agents + Teams)
  const handleGlobalExport = async () => {
    if (!metrics || !currentStartDate || !currentEndDate) return;

    const files: Array<{ filename: string; content: string }> = [];
    const dateStr = formatDateForFilename();
    const sectionName = metricsType.charAt(0).toUpperCase() + metricsType.slice(1);

    try {
      // Export global summary metrics
      if (metricsType === 'calls') {
        const summaryMetrics = [
          { label: 'Total Calls', value: metrics.totalCalls, previousValue: comparison?.totalCalls },
          { label: 'Inbound Calls', value: metrics.inboundCalls, previousValue: comparison?.inboundCalls },
          { label: 'Outbound Calls', value: metrics.outboundCalls, previousValue: comparison?.outboundCalls },
          { label: 'Answered Calls', value: metrics.answeredCalls, previousValue: comparison?.answeredCalls },
          { label: 'Missed Calls', value: metrics.missedCalls, previousValue: comparison?.missedCalls },
          { label: 'Average Duration (seconds)', value: metrics.averageDuration, previousValue: comparison?.averageDuration },
          { label: 'Average Wait Time (seconds)', value: metrics.averageWaitTime, previousValue: comparison?.averageWaitTime },
          { label: 'Average Answer Time (seconds)', value: metrics.averageAnswerTime, previousValue: comparison?.averageAnswerTime },
        ];
        files.push({
          filename: `${dateStr}-calls-global-summary.csv`,
          content: metricsToCSV(summaryMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
        });
      } else if (metricsType === 'voicemails') {
        const summaryMetrics = [
          { label: 'Total Voicemails', value: metrics.totalVoicemails, previousValue: comparison?.totalVoicemails },
          { label: 'Inbound', value: metrics.inboundVoicemails, previousValue: comparison?.inboundVoicemails },
          { label: 'Outbound', value: metrics.outboundVoicemails, previousValue: comparison?.outboundVoicemails },
          { label: 'Average Duration (seconds)', value: metrics.averageDuration, previousValue: comparison?.averageDuration },
        ];
        files.push({
          filename: `${dateStr}-voicemails-global-summary.csv`,
          content: metricsToCSV(summaryMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
        });
      } else if (metricsType === 'sms') {
        const summaryMetrics = [
          { label: 'Total Messages', value: metrics.totalMessages, previousValue: comparison?.totalMessages },
          { label: 'Inbound', value: metrics.inboundMessages, previousValue: comparison?.inboundMessages },
          { label: 'Outbound', value: metrics.outboundMessages, previousValue: comparison?.outboundMessages },
          { label: 'Average Length (chars)', value: metrics.averageLength, previousValue: comparison?.averageLength },
          { label: 'Max Length (chars)', value: metrics.maxLength, previousValue: comparison?.maxLength },
          { label: 'Min Length (chars)', value: metrics.minLength, previousValue: comparison?.minLength },
        ];
        files.push({
          filename: `${dateStr}-sms-global-summary.csv`,
          content: metricsToCSV(summaryMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
        });
      }

      // Export ALL subsections data inline to avoid multiple zips
      // OVERVIEW subsection
      if (metricsType === 'calls') {
        // Key metrics
        const keyMetrics = [
          { label: 'Total Calls', value: metrics.totalCalls, previousValue: comparison?.totalCalls },
          { label: 'Inbound', value: metrics.inboundCalls, previousValue: comparison?.inboundCalls },
          { label: 'Outbound', value: metrics.outboundCalls, previousValue: comparison?.outboundCalls },
          { label: 'Answered', value: metrics.answeredCalls, previousValue: comparison?.answeredCalls },
          { label: 'Missed', value: metrics.missedCalls, previousValue: comparison?.missedCalls },
          { label: 'Average Duration (seconds)', value: metrics.averageDuration, previousValue: comparison?.averageDuration },
          { label: 'Average Wait Time (seconds)', value: metrics.averageWaitTime, previousValue: comparison?.averageWaitTime },
          { label: 'Average Answer Time (seconds)', value: metrics.averageAnswerTime, previousValue: comparison?.averageAnswerTime },
        ];
        files.push({
          filename: `${dateStr}-calls-overview-key-metrics.csv`,
          content: metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
        });

        // Inbound metrics
        if (metrics.inboundDetails) {
          const inboundMetrics = [
            { label: 'Answered', value: metrics.inboundDetails.answered, previousValue: comparison?.inboundDetails?.answered },
            { label: 'Missed', value: metrics.inboundDetails.missed, previousValue: comparison?.inboundDetails?.missed },
            { label: 'Abandoned', value: metrics.inboundDetails.abandoned, previousValue: comparison?.inboundDetails?.abandoned },
            { label: 'Answer Rate (%)', value: metrics.inboundDetails.answerRate?.toFixed(1) ?? '0.0', previousValue: comparison?.inboundDetails?.answerRate?.toFixed(1) },
          ];
          files.push({
            filename: `${dateStr}-calls-overview-inbound.csv`,
            content: metricsToCSV(inboundMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });
        }

        // Outbound metrics
        if (metrics.outboundDetails) {
          const outboundMetrics = [
            { label: 'Completed', value: metrics.outboundDetails.completed, previousValue: comparison?.outboundDetails?.completed },
            { label: 'No Answer', value: metrics.outboundDetails.noAnswer, previousValue: comparison?.outboundDetails?.noAnswer },
            { label: 'Failed', value: metrics.outboundDetails.failed, previousValue: comparison?.outboundDetails?.failed },
            { label: 'Success Rate (%)', value: metrics.outboundDetails.successRate?.toFixed(1) ?? '0.0', previousValue: comparison?.outboundDetails?.successRate?.toFixed(1) },
          ];
          files.push({
            filename: `${dateStr}-calls-overview-outbound.csv`,
            content: metricsToCSV(outboundMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });
        }

        // Quality metrics
        if (metrics.ratingMetrics) {
          const qualityMetrics = [
            { label: 'Evaluated Calls', value: metrics.ratingMetrics.evaluatedCalls, previousValue: comparison?.ratingMetrics?.evaluatedCalls },
            { label: 'Unevaluated Calls', value: metrics.ratingMetrics.unevaluatedCalls, previousValue: comparison?.ratingMetrics?.unevaluatedCalls },
            { label: 'Evaluation Rate (%)', value: metrics.ratingMetrics.evaluationRate?.toFixed(1) ?? '0.0', previousValue: comparison?.ratingMetrics?.evaluationRate?.toFixed(1) },
            { label: 'Average Rating', value: metrics.ratingMetrics.averageRating?.toFixed(2) ?? '0.00', previousValue: comparison?.ratingMetrics?.averageRating?.toFixed(2) },
            { label: 'Positive Rating Rate (%)', value: metrics.ratingMetrics.positiveRatingRate?.toFixed(1) ?? '0.0', previousValue: comparison?.ratingMetrics?.positiveRatingRate?.toFixed(1) },
          ];
          files.push({
            filename: `${dateStr}-calls-overview-quality-metrics.csv`,
            content: metricsToCSV(qualityMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });
        }

        // Tables
        if (metrics.callsByStatus && metrics.callsByStatus.length > 0) {
          files.push({ filename: `${dateStr}-calls-overview-by-status.csv`, content: tableToCSV(
            [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }],
            metrics.callsByStatus.map((s: any) => ({ status: s.status, count: s.count }))
          )});
        }

        if (metrics.callsByPurpose && metrics.callsByPurpose.length > 0) {
          files.push({ filename: `${dateStr}-calls-overview-by-purpose.csv`, content: tableToCSV(
            [{ key: 'purpose', label: 'Purpose' }, { key: 'totalCalls', label: 'Total' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
            metrics.callsByPurpose.map((p: any) => ({ purpose: p.purpose || 'Unknown', totalCalls: p.totalCalls, inbound: p.inboundCalls, outbound: p.outboundCalls }))
          )});
        }
      } else if (metricsType === 'voicemails') {
        // Key metrics
        const keyMetrics = [
          { label: 'Total Voicemails', value: metrics.totalVoicemails, previousValue: comparison?.totalVoicemails },
          { label: 'Inbound', value: metrics.inboundVoicemails, previousValue: comparison?.inboundVoicemails },
          { label: 'Outbound', value: metrics.outboundVoicemails, previousValue: comparison?.outboundVoicemails },
          { label: 'Avg Duration (seconds)', value: metrics.averageDuration, previousValue: comparison?.averageDuration },
        ];
        files.push({
          filename: `${dateStr}-voicemails-overview-key-metrics.csv`,
          content: metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
        });

        // Tables
        if (metrics.voicemailsByStatus && metrics.voicemailsByStatus.length > 0) {
          files.push({ filename: `${dateStr}-voicemails-overview-by-status.csv`, content: tableToCSV(
            [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }],
            metrics.voicemailsByStatus.map((s: any) => ({ status: s.status, count: s.count }))
          )});
        }
      } else if (metricsType === 'sms') {
        // Key metrics
        const keyMetrics = [
          { label: 'Total Messages', value: metrics.totalMessages, previousValue: comparison?.totalMessages },
          { label: 'Inbound', value: metrics.inboundMessages, previousValue: comparison?.inboundMessages },
          { label: 'Outbound', value: metrics.outboundMessages, previousValue: comparison?.outboundMessages },
          { label: 'Avg Length (chars)', value: metrics.averageLength, previousValue: comparison?.averageLength },
          { label: 'Max Length (chars)', value: metrics.maxLength, previousValue: comparison?.maxLength },
          { label: 'Min Length (chars)', value: metrics.minLength, previousValue: comparison?.minLength },
        ];
        files.push({
          filename: `${dateStr}-sms-overview-key-metrics.csv`,
          content: metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
        });

        // Tables
        if (metrics.messagesByStatus && metrics.messagesByStatus.length > 0) {
          files.push({ filename: `${dateStr}-sms-overview-by-status.csv`, content: tableToCSV(
            [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
            metrics.messagesByStatus.map((s: any) => ({ status: s.status, count: s.count, inbound: s.inboundCount, outbound: s.outboundCount }))
          )});
        }

        if (metrics.messagesByTrackingNumber && metrics.messagesByTrackingNumber.length > 0) {
          files.push({ filename: `${dateStr}-sms-overview-by-tracking-number.csv`, content: tableToCSV(
            [{ key: 'label', label: 'Number Label' }, { key: 'count', label: 'Count' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
            metrics.messagesByTrackingNumber.slice(0, 10).map((tn: any) => ({ label: tn.label, count: tn.count, inbound: tn.inboundCount, outbound: tn.outboundCount }))
          )});
        }

        if (metrics.messagesByCategory && metrics.messagesByCategory.length > 0) {
          files.push({ filename: `${dateStr}-sms-overview-by-category.csv`, content: tableToCSV(
            [{ key: 'category', label: 'Category' }, { key: 'count', label: 'Count' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
            metrics.messagesByCategory.map((c: any) => ({ category: c.category, count: c.count, inbound: c.inboundCount, outbound: c.outboundCount }))
          )});
        }
      }

      // AGENTS subsection
      if (metricsType === 'calls' && metrics.callsByAgent && metrics.callsByAgent.length > 0) {
        files.push({ filename: `${dateStr}-calls-agents.csv`, content: tableToCSV(
          [{ key: 'agentName', label: 'Agent' }, { key: 'total', label: 'Total Calls' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
          metrics.callsByAgent.map((a: any) => ({ agentName: a.agentName || 'Unknown', total: a.count, inbound: a.inboundCount, outbound: a.outboundCount }))
        )});
      } else if (metricsType === 'voicemails' && metrics.voicemailsByAgent && metrics.voicemailsByAgent.length > 0) {
        files.push({ filename: `${dateStr}-voicemails-agents.csv`, content: tableToCSV(
          [{ key: 'agentName', label: 'Agent' }, { key: 'total', label: 'Total' }, { key: 'inbound', label: 'Inbound' }, { key: 'outbound', label: 'Outbound' }],
          metrics.voicemailsByAgent.map((a: any) => ({ agentName: a.agentName || 'Unknown', total: a.count, inbound: a.inboundCount, outbound: a.outboundCount }))
        )});
      } else if (metricsType === 'sms' && metrics.messagesByAgent && metrics.messagesByAgent.length > 0) {
        files.push({ filename: `${dateStr}-sms-agents.csv`, content: tableToCSV(
          [{ key: 'agentName', label: 'Agent' }, { key: 'sent', label: 'Sent' }, { key: 'received', label: 'Received' }, { key: 'total', label: 'Total' }],
          metrics.messagesByAgent.map((a: any) => ({
            agentName: a.agentName || a.agent_name || 'Unknown',
            sent: a.sent_messages || a.sentMessages || a.outbound || 0,
            received: a.received_messages || a.receivedMessages || a.inbound || 0,
            total: a.total_messages || a.totalMessages || a.count || 0
          }))
        )});
      }

      // TEAMS subsection
      if (metricsType === 'calls' && metrics.callsByTeam && metrics.callsByTeam.length > 0) {
        metrics.callsByTeam.forEach((team: any) => {
          const comparisonTeam = comparison?.callsByTeam?.find((t: any) => t.teamId === team.teamId);
          const teamMetrics = [
            { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
            { label: 'Total Calls', value: team.totalCalls, previousValue: comparisonTeam?.totalCalls },
            { label: 'Inbound', value: team.inboundCalls, previousValue: comparisonTeam?.inboundCalls },
            { label: 'Outbound', value: team.outboundCalls, previousValue: comparisonTeam?.outboundCalls },
            { label: 'Answered', value: team.answeredCalls, previousValue: comparisonTeam?.answeredCalls },
            { label: 'Average Duration (seconds)', value: team.averageDuration, previousValue: comparisonTeam?.averageDuration },
          ];
          files.push({
            filename: `${dateStr}-calls-team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}.csv`,
            content: metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });
        });
      } else if (metricsType === 'voicemails' && metrics.voicemailsByTeam && metrics.voicemailsByTeam.length > 0) {
        metrics.voicemailsByTeam.forEach((team: any) => {
          const comparisonTeam = comparison?.voicemailsByTeam?.find((t: any) => t.teamId === team.teamId);
          const teamMetrics = [
            { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
            { label: 'Total Voicemails', value: team.totalVoicemails, previousValue: comparisonTeam?.totalVoicemails },
            { label: 'Inbound', value: team.inboundVoicemails, previousValue: comparisonTeam?.inboundVoicemails },
            { label: 'Outbound', value: team.outboundVoicemails, previousValue: comparisonTeam?.outboundVoicemails },
          ];
          files.push({
            filename: `${dateStr}-voicemails-team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}.csv`,
            content: metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });
        });
      } else if (metricsType === 'sms' && metrics.messagesByTeam && metrics.messagesByTeam.length > 0) {
        metrics.messagesByTeam.forEach((team: any) => {
          const comparisonTeam = comparison?.messagesByTeam?.find((t: any) => t.teamId === team.teamId);
          const teamMetrics = [
            { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
            { label: 'Total Messages', value: team.totalMessages, previousValue: comparisonTeam?.totalMessages },
            { label: 'Inbound', value: team.inboundMessages, previousValue: comparisonTeam?.inboundMessages },
            { label: 'Outbound', value: team.outboundMessages, previousValue: comparisonTeam?.outboundMessages },
          ];
          files.push({
            filename: `${dateStr}-sms-team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}.csv`,
            content: metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate)
          });
        });
      }

      if (files.length > 0) {
        await downloadZipFile(files, `${dateStr}-${sectionName}-Complete.zip`);
        notify(`Exported complete ${sectionName} section (${files.length} file(s))`, { type: 'success' });
      } else {
        notify('No data available to export', { type: 'warning' });
      }
    } catch (error) {
      console.error('Export error:', error);
      notify('Error exporting data', { type: 'error' });
    }
  };

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const dateRange = getDateRange(datePreset);

      if (!dateRange) {
        setLoading(false);
        return;
      }

      const { startDate, endDate } = dateRange;

      // Store current date range
      setCurrentStartDate(startDate);
      setCurrentEndDate(endDate);

      const params: any = {
        startDate,
        endDate,
      };

      if (compareEnabled) {
        const { compareStartDate, compareEndDate } = getPreviousPeriod(
          datePreset,
          startDate,
          endDate,
        );
        params.compareStartDate = compareStartDate;
        params.compareEndDate = compareEndDate;

        // Store previous date range
        setPreviousStartDate(compareStartDate);
        setPreviousEndDate(compareEndDate);
      } else {
        // Clear previous dates when comparison is disabled
        setPreviousStartDate('');
        setPreviousEndDate('');
      }

      let response;
      switch (metricsType) {
        case 'calls':
          response = await callsService.getCallMetrics(params);
          break;
        case 'voicemails':
          response = await callsService.getVoicemailMetrics(params);
          break;
        case 'sms':
          response = await callsService.getSmsMetrics(params);
          break;
      }

      if (response.success && response.data) {
        setMetrics(response.data.current);
        setComparison(response.data.comparison);
      } else {
        notify('Failed to fetch metrics', { type: 'error' });
      }
    } catch (error) {
      notify('Failed to fetch metrics', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    metricsType,
    datePreset,
    compareEnabled,
    getDateRange,
    getPreviousPeriod,
    notify,
    customStartDate,
    customEndDate,
  ]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Navigate to previous period
  const goToPreviousPeriod = useCallback(() => {
    if (datePreset === 'custom') {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const duration = end.getTime() - start.getTime();

      const newEnd = new Date(start);
      newEnd.setMilliseconds(-1);
      const newStart = new Date(newEnd.getTime() - duration);

      setCustomStartDate(newStart.toISOString().split('T')[0]);
      setCustomEndDate(newEnd.toISOString().split('T')[0]);
    } else {
      const now = new Date();
      let newEnd: Date;

      switch (datePreset) {
        case 'today':
          newEnd = new Date(now);
          newEnd.setDate(newEnd.getDate() - 1);
          setCustomStartDate(newEnd.toISOString().split('T')[0]);
          setCustomEndDate(newEnd.toISOString().split('T')[0]);
          setDatePreset('custom');
          break;
        case 'last7days':
          newEnd = new Date(now);
          newEnd.setDate(newEnd.getDate() - 7);
          const newStart7 = new Date(newEnd);
          newStart7.setDate(newStart7.getDate() - 7);
          setCustomStartDate(newStart7.toISOString().split('T')[0]);
          setCustomEndDate(newEnd.toISOString().split('T')[0]);
          setDatePreset('custom');
          break;
        case 'last30days':
          newEnd = new Date(now);
          newEnd.setDate(newEnd.getDate() - 30);
          const newStart30 = new Date(newEnd);
          newStart30.setDate(newStart30.getDate() - 30);
          setCustomStartDate(newStart30.toISOString().split('T')[0]);
          setCustomEndDate(newEnd.toISOString().split('T')[0]);
          setDatePreset('custom');
          break;
      }
    }
  }, [datePreset, customStartDate, customEndDate]);

  // Navigate to next period
  const goToNextPeriod = useCallback(() => {
    if (datePreset === 'custom') {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const duration = end.getTime() - start.getTime();

      const newStart = new Date(end);
      newStart.setMilliseconds(1);
      const newEnd = new Date(newStart.getTime() + duration);

      // Don't allow going into the future
      const now = new Date();
      if (newEnd > now) {
        notify('Cannot navigate to future dates', { type: 'warning' });
        return;
      }

      setCustomStartDate(newStart.toISOString().split('T')[0]);
      setCustomEndDate(newEnd.toISOString().split('T')[0]);
    } else {
      const now = new Date();
      let newStart: Date;
      let newEnd: Date;

      switch (datePreset) {
        case 'today':
          newStart = new Date(now);
          newStart.setDate(newStart.getDate() + 1);
          if (newStart > now) {
            notify('Cannot navigate to future dates', { type: 'warning' });
            return;
          }
          setCustomStartDate(newStart.toISOString().split('T')[0]);
          setCustomEndDate(newStart.toISOString().split('T')[0]);
          setDatePreset('custom');
          break;
        case 'last7days':
          newEnd = new Date(now);
          newStart = new Date(newEnd);
          newStart.setDate(newStart.getDate() - 7);
          // Moving forward would be "today - 7 days", which is just current state
          notify('Already at the most recent 7-day period', { type: 'info' });
          break;
        case 'last30days':
          newEnd = new Date(now);
          newStart = new Date(newEnd);
          newStart.setDate(newStart.getDate() - 30);
          // Moving forward would be "today - 30 days", which is just current state
          notify('Already at the most recent 30-day period', { type: 'info' });
          break;
      }
    }
  }, [datePreset, customStartDate, customEndDate, notify]);

  const renderMetricCard = (
    title: string | ReactNode,
    value: number | string,
    comparison?: number | string,
    rawValue?: number,
    invertColors: boolean = false, // true for metrics where decrease is good
    formatComparison?: (val: number) => string, // optional formatter for comparison value
  ) => {
    const hasComparison = compareEnabled && comparison !== undefined && comparison !== null;

    // Use rawValue if provided (for formatted durations), otherwise parse value
    const numericValue = rawValue !== undefined ? rawValue : Number(value);
    const isValidNumber = !isNaN(numericValue) && isFinite(numericValue);

    // Parse comparison value as number
    const comparisonNum = typeof comparison === 'number' ? comparison : Number(comparison);
    const isValidComparison = !isNaN(comparisonNum) && isFinite(comparisonNum);

    // Format comparison value
    let displayComparison: string | number | undefined;
    if (comparison !== undefined) {
      if (formatComparison && isValidComparison) {
        // Use custom formatter if provided
        displayComparison = formatComparison(comparisonNum);
      } else if (rawValue !== undefined && isValidComparison) {
        // Duration formatting (legacy behavior)
        displayComparison = formatDuration(comparisonNum);
      } else {
        // Default: use raw value
        displayComparison = comparison;
      }
    }

    // Calculate percentage change only if we have valid numbers and comparison is not 0
    let percentChange = 0;
    let showPercentChange = false;

    if (hasComparison && isValidNumber && isValidComparison) {
      if (comparisonNum === 0) {
        // If comparison is 0, show "new" or infinite increase
        showPercentChange = numericValue > 0;
        percentChange = Infinity;
      } else {
        percentChange = ((numericValue - comparisonNum) / comparisonNum) * 100;
        showPercentChange = true;
      }
    }

    // Determine color based on semantic meaning
    const isIncrease = percentChange >= 0;
    const isPositive = invertColors ? !isIncrease : isIncrease;
    const changeColor = isPositive ? '#2E7D32' : '#E42020';

    return (
      <Card>
        <CardContent>
          <Typography color='textSecondary' gutterBottom component='div'>
            {title}
          </Typography>
          <Typography
            component='div'
            sx={{ color: '#1C1B1F', fontSize: '1.5rem', lineHeight: 1.4 }}>
            {hasComparison ? (
              <>
                <span style={{ color: '#1C1B1F' }}>{value}</span>
                <span style={{ color: '#757575', fontSize: '0.9em' }}> / </span>
                <span style={{ color: '#1C1B1F', opacity: 0.5 }}>{displayComparison}</span>
              </>
            ) : (
              value
            )}
          </Typography>
          {hasComparison && showPercentChange && (
            <Typography variant='body2' sx={{ color: changeColor, mt: 1 }}>
              {percentChange === Infinity ? (
                <>↑ New</>
              ) : (
                <>
                  {percentChange >= 0 ? '↑' : '↓'} {Math.abs(percentChange).toFixed(1)}% vs previous
                  period
                </>
              )}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Helper to parse duration strings like "1m 13s" to total seconds
  const parseDuration = (value: string | number): number => {
    if (typeof value === 'number') return value;

    const str = String(value);
    // Check if it's a duration format like "1m 13s"
    const durationMatch = str.match(/(\d+)m\s*(\d+)s/);
    if (durationMatch) {
      const mins = parseInt(durationMatch[1], 10);
      const secs = parseInt(durationMatch[2], 10);
      return mins * 60 + secs;
    }

    // Check if it's a chars format like "327 chars"
    const charsMatch = str.match(/(\d+)\s*chars?/);
    if (charsMatch) {
      return parseInt(charsMatch[1], 10);
    }

    // Otherwise try standard parseFloat
    return parseFloat(str);
  };

  // Helper to format value with comparison (returns JSX for table cells with colors)
  const formatWithComparison = (
    current: number | string,
    previous: number | string | undefined,
  ): React.ReactNode => {
    if (!compareEnabled || previous === undefined || previous === null) {
      return String(current);
    }

    const currentNum = parseDuration(current);
    const previousNum = parseDuration(previous);

    if (isNaN(currentNum) || isNaN(previousNum)) {
      return String(current);
    }

    let changeElement = null;
    if (previousNum === 0) {
      if (currentNum > 0) {
        changeElement = <span style={{ color: '#2E7D32', fontWeight: 'bold' }}> (↑ New)</span>;
      }
    } else {
      const change = ((currentNum - previousNum) / previousNum) * 100;
      const arrow = change >= 0 ? '↑' : '↓';
      const color = change >= 0 ? '#2E7D32' : '#E42020'; // Green for increase, red for decrease
      changeElement = (
        <span style={{ color, fontWeight: 'bold' }}>
          {' '}
          ({arrow} {Math.abs(change).toFixed(0)}%)
        </span>
      );
    }

    return (
      <>
        <span style={{ color: '#1C1B1F' }}>{current}</span>
        <span style={{ color: '#757575' }}> / </span>
        <span style={{ color: '#1C1B1F', opacity: 0.5 }}>{previous}</span>
        {changeElement}
      </>
    );
  };

  // Helper function to format comparison with values in two rows
  const formatWithComparisonTwoRows = (
    current: number | string,
    previous: number | string | undefined,
  ): JSX.Element => {
    if (!compareEnabled || previous === undefined || previous === null) {
      return <>{String(current)}</>;
    }

    const currentNum = parseDuration(current);
    const previousNum = parseDuration(previous);

    if (isNaN(currentNum) || isNaN(previousNum)) {
      return <>{String(current)}</>;
    }

    let changeText = '';
    let changeColor = '#757575';

    if (previousNum === 0) {
      if (currentNum > 0) {
        changeText = '↑ New';
        changeColor = '#2E7D32';
      }
    } else {
      const change = ((currentNum - previousNum) / previousNum) * 100;
      const arrow = change >= 0 ? '↑' : '↓';
      changeColor = change >= 0 ? '#2E7D32' : '#E42020';
      changeText = `${arrow} ${Math.abs(change).toFixed(0)}%`;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ color: '#1C1B1F' }}>
          {current}
          <span style={{ color: '#757575', margin: '0 4px' }}>/</span>
          <span style={{ color: '#1C1B1F', opacity: 0.5 }}>{previous}</span>
        </div>
        {changeText && (
          <div style={{ fontSize: '0.85em', color: changeColor, fontWeight: 'bold' }}>
            {changeText}
          </div>
        )}
      </div>
    );
  };

  // Helper to find comparison data for an agent
  const findAgentComparison = <T extends { agentId: number }>(
    agentId: number,
    comparisonList: T[] | undefined,
  ): T | undefined => {
    if (!compareEnabled || !comparisonList) return undefined;
    return comparisonList.find((a) => a.agentId === agentId);
  };

  return (
    <Box className='tab-section metrics-center-section' marginTop={0}>
      <Box className='metrics-center-header'>
        <Box display='flex' justifyContent='space-between' alignContent='center' marginBottom='8px'>
          {breadcrumbs}
        </Box>

        {/* Filters */}
        <Box display='flex' alignItems='flex-start' gap={2} mt={2} mb={3} flexWrap='wrap'>
          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={datePreset}
              label='Time Period'
              onChange={(e) => setDatePreset(e.target.value as DatePreset)}>
              <MenuItem value='today'>Today</MenuItem>
              <MenuItem value='last7days'>Last 7 Days</MenuItem>
              <MenuItem value='last30days'>Last 30 Days</MenuItem>
              <MenuItem value='custom'>Custom Range</MenuItem>
            </Select>
          </FormControl>

          {/* Previous Period Button */}
          <IconButton
            size='small'
            onClick={goToPreviousPeriod}
            title='Previous Period'
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}>
            <ChevronLeft />
          </IconButton>

          {datePreset === 'custom' && (
            <>
              <TextField
                label='Start Date'
                type='date'
                size='small'
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label='End Date'
                type='date'
                size='small'
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: customStartDate,
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={{ minWidth: 160 }}
              />
            </>
          )}

          {/* Next Period Button */}
          <IconButton
            size='small'
            onClick={goToNextPeriod}
            title='Next Period'
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}>
            <ChevronRight />
          </IconButton>

          <Button
            variant={compareEnabled ? 'contained' : 'outlined'}
            size='small'
            onClick={() => setCompareEnabled(!compareEnabled)}>
            {compareEnabled ? 'Hide Comparison' : 'Compare with Previous Period'}
          </Button>

          <Button variant='outlined' size='small' onClick={fetchMetrics} disabled={loading}>
            Refresh
          </Button>

          <Button
            variant='outlined'
            size='small'
            onClick={handleGlobalExport}
            disabled={!metrics || loading}
            startIcon={<DownloadIcon />}
            sx={{ color: '#1a667a', borderColor: '#1a667a', '&:hover': { borderColor: '#1a667a', backgroundColor: 'rgba(26, 102, 122, 0.04)' } }}>
            Export All
          </Button>

          {compareEnabled && (
            <Box
              display='flex'
              alignItems='center'
              gap={2}
              sx={{
                ml: 'auto',
                padding: '8px 16px',
                backgroundColor: '#FAFAFA',
                borderRadius: '4px',
                border: '1px solid #E0E0E0',
              }}>
              <Typography variant='body2' fontWeight='600' color='#1C1B1F'>
                Legend:
              </Typography>
              <Box display='flex' alignItems='center' gap={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: '#1C1B1F',
                    borderRadius: '2px',
                  }}
                />
                <Typography variant='body2' color='#1C1B1F'>
                  Current Period
                </Typography>
              </Box>
              <Box display='flex' alignItems='center' gap={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: '#9E9E9E',
                    borderRadius: '2px',
                  }}
                />
                <Typography variant='body2' color='#757575'>
                  Previous Period
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Metrics Type Tabs */}
        <Tabs value={metricsType} onChange={(_, newValue) => setMetricsType(newValue)}>
          <Tab label='Calls' value='calls' />
          <Tab label='Voicemails' value='voicemails' />
          <Tab label='SMS' value='sms' />
        </Tabs>

        {/* Calls Sub-Tabs */}
        {metricsType === 'calls' && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs
              value={callsSubTab}
              onChange={(_, newValue) => setCallsSubTab(newValue)}
              sx={{ minHeight: '40px' }}>
              <Tab label='📊 Overview' value='overview' sx={{ minHeight: '40px' }} />
              <Tab label='👥 Agents' value='agents' sx={{ minHeight: '40px' }} />
              <Tab label='🏢 Teams' value='teams' sx={{ minHeight: '40px' }} />
            </Tabs>
            <Button
              variant='text'
              size='small'
              onClick={() => exportSubsection(callsSubTab)}
              startIcon={<DownloadIcon />}
              sx={{ color: '#1a667a', minHeight: '40px' }}>
              Export {callsSubTab.charAt(0).toUpperCase() + callsSubTab.slice(1)}
            </Button>
          </Box>
        )}
      </Box>

      {/* Metrics Display */}
      {loading ? (
        <Typography>Loading metrics...</Typography>
      ) : metrics ? (
        <Box mt={3}>
          {/* Call Metrics - Tab-based Layout */}
          {metricsType === 'calls' && callsSubTab === 'overview' && (
            <OverviewTab
              metrics={metrics}
              comparison={comparison}
              renderMetricCard={renderMetricCard}
              formatDuration={formatDuration}
              formatWithComparison={formatWithComparison}
              currentStartDate={currentStartDate}
              currentEndDate={currentEndDate}
              previousStartDate={previousStartDate}
              previousEndDate={previousEndDate}
            />
          )}

          {metricsType === 'calls' &&
            callsSubTab === 'agents' &&
            metrics.callsByAgent &&
            Array.isArray(metrics.callsByAgent) &&
            metrics.callsByAgent.length > 0 && (
              <AgentsTab
                agents={metrics.callsByAgent}
                comparison={comparison?.callsByAgent}
                formatWithComparisonTwoRows={formatWithComparisonTwoRows}
                formatDuration={formatDuration}
                findAgentComparison={findAgentComparison}
              />
            )}

          {metricsType === 'calls' &&
            callsSubTab === 'teams' &&
            metrics.callsByTeam &&
            Array.isArray(metrics.callsByTeam) &&
            metrics.callsByTeam.length > 0 && (
              <TeamsTab
                teams={metrics.callsByTeam}
                comparison={comparison?.callsByTeam}
                renderMetricCard={renderMetricCard}
                formatDuration={formatDuration}
                currentStartDate={currentStartDate}
                currentEndDate={currentEndDate}
                previousStartDate={previousStartDate}
                previousEndDate={previousEndDate}
              />
            )}

          {/* Voicemails Section with Tabs */}
          {metricsType === 'voicemails' && (
            <>
              {/* Voicemails Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tabs value={voicemailsSubTab} onChange={(_, newValue) => setVoicemailsSubTab(newValue)} sx={{ minHeight: '40px' }}>
                  <Tab label='📊 Overview' value='overview' sx={{ minHeight: '40px' }} />
                  <Tab label='👤 Agents' value='agents' sx={{ minHeight: '40px' }} />
                  <Tab label='👥 Teams' value='teams' sx={{ minHeight: '40px' }} />
                </Tabs>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => exportSubsection(voicemailsSubTab)}
                  startIcon={<DownloadIcon />}
                  sx={{ color: '#1a667a', minHeight: '40px' }}>
                  Export {voicemailsSubTab.charAt(0).toUpperCase() + voicemailsSubTab.slice(1)}
                </Button>
              </Box>

              {voicemailsSubTab === 'overview' && (
                <VoicemailsOverviewTab
                  metrics={metrics}
                  comparison={comparison}
                  formatWithComparison={formatWithComparison}
                  formatDuration={formatDuration}
                  renderMetricCard={renderMetricCard}
                  currentStartDate={currentStartDate}
                  currentEndDate={currentEndDate}
                  previousStartDate={previousStartDate}
                  previousEndDate={previousEndDate}
                />
              )}

              {voicemailsSubTab === 'agents' &&
                metrics.voicemailsByAgent &&
                Array.isArray(metrics.voicemailsByAgent) &&
                metrics.voicemailsByAgent.length > 0 && (
                  <VoicemailsAgentsTab
                    agents={metrics.voicemailsByAgent}
                    comparison={comparison?.voicemailsByAgent}
                    formatWithComparisonTwoRows={formatWithComparisonTwoRows}
                    formatDuration={formatDuration}
                    findAgentComparison={findAgentComparison}
                  />
                )}

              {voicemailsSubTab === 'teams' &&
                metrics.voicemailsByTeam &&
                Array.isArray(metrics.voicemailsByTeam) &&
                metrics.voicemailsByTeam.length > 0 && (
                  <VoicemailsTeamsTab
                    teams={metrics.voicemailsByTeam}
                    comparison={comparison?.voicemailsByTeam}
                    renderMetricCard={renderMetricCard}
                    formatDuration={formatDuration}
                    currentStartDate={currentStartDate}
                    currentEndDate={currentEndDate}
                    previousStartDate={previousStartDate}
                    previousEndDate={previousEndDate}
                  />
                )}
            </>
          )}

          {/* SMS Section with Tabs */}
          {metricsType === 'sms' && (
            <>
              {/* SMS Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tabs value={smsSubTab} onChange={(_, newValue) => setSmsSubTab(newValue)} sx={{ minHeight: '40px' }}>
                  <Tab label='📊 Overview' value='overview' sx={{ minHeight: '40px' }} />
                  <Tab label='👤 Agents' value='agents' sx={{ minHeight: '40px' }} />
                  <Tab label='👥 Teams' value='teams' sx={{ minHeight: '40px' }} />
                </Tabs>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => exportSubsection(smsSubTab)}
                  startIcon={<DownloadIcon />}
                  sx={{ color: '#1a667a', minHeight: '40px' }}>
                  Export {smsSubTab.charAt(0).toUpperCase() + smsSubTab.slice(1)}
                </Button>
              </Box>

              {smsSubTab === 'overview' && (
                <SmsOverviewTab
                  metrics={metrics}
                  comparison={comparison}
                  formatWithComparison={formatWithComparison}
                  renderMetricCard={renderMetricCard}
                  currentStartDate={currentStartDate}
                  currentEndDate={currentEndDate}
                  previousStartDate={previousStartDate}
                  previousEndDate={previousEndDate}
                />
              )}

              {smsSubTab === 'agents' &&
                metrics.messagesByAgent &&
                Array.isArray(metrics.messagesByAgent) &&
                metrics.messagesByAgent.length > 0 && (
                  <SmsAgentsTab
                    agents={metrics.messagesByAgent}
                    comparison={comparison?.messagesByAgent}
                    formatWithComparison={formatWithComparison}
                    findAgentComparison={findAgentComparison}
                  />
                )}

              {smsSubTab === 'teams' &&
                metrics.messagesByTeam &&
                Array.isArray(metrics.messagesByTeam) &&
                metrics.messagesByTeam.length > 0 && (
                  <SmsTeamsTab
                    teams={metrics.messagesByTeam}
                    comparison={comparison?.messagesByTeam}
                    renderMetricCard={renderMetricCard}
                    currentStartDate={currentStartDate}
                    currentEndDate={currentEndDate}
                    previousStartDate={previousStartDate}
                    previousEndDate={previousEndDate}
                  />
                )}
            </>
          )}

          {/* Calls by Purpose Table */}
          {metricsType === 'calls' &&
            metrics.callsByPurpose &&
            metrics.callsByPurpose.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <CustomTable
                  title='Calls by Purpose'
                  columns={[
                    { key: 'purpose', label: 'Purpose' },
                    { key: 'totalCalls', label: 'Total' },
                    { key: 'inbound', label: 'Inbound' },
                    { key: 'outbound', label: 'Outbound' },
                    { key: 'completionRate', label: 'Completion Rate' },
                    { key: 'avgDuration', label: 'Avg Duration' },
                    { key: 'avgRating', label: 'Avg Rating' },
                    { key: 'positiveRate', label: 'Positive Rate' },
                  ]}
                  rows={metrics.callsByPurpose.slice(0, 15).map((p: any) => {
                    const prevData = comparison?.callsByPurpose?.find(
                      (prev: any) => prev.purpose === p.purpose,
                    );

                    return {
                      purpose: p.purpose || 'Unknown',
                      totalCalls: formatWithComparison(p.totalCalls, prevData?.totalCalls),
                      inbound: formatWithComparison(p.inboundCalls, prevData?.inboundCalls),
                      outbound: formatWithComparison(p.outboundCalls, prevData?.outboundCalls),
                      completionRate: formatWithComparison(
                        `${p.completionRate.toFixed(1)}%`,
                        prevData?.completionRate
                          ? `${prevData.completionRate.toFixed(1)}%`
                          : undefined,
                      ),
                      avgDuration: formatWithComparison(
                        formatDuration(p.averageDuration),
                        prevData?.averageDuration
                          ? formatDuration(prevData.averageDuration)
                          : undefined,
                      ),
                      avgRating:
                        p.evaluatedCalls > 0
                          ? formatWithComparison(
                              `${p.averageRating.toFixed(2)} ⭐`,
                              prevData?.averageRating
                                ? `${prevData.averageRating.toFixed(2)} ⭐`
                                : undefined,
                            )
                          : 'N/A',
                      positiveRate:
                        p.evaluatedCalls > 0
                          ? formatWithComparison(
                              `${p.positiveRatingRate.toFixed(1)}%`,
                              prevData?.positiveRatingRate
                                ? `${prevData.positiveRatingRate.toFixed(1)}%`
                                : undefined,
                            )
                          : 'N/A',
                    };
                  })}
                  defaultText='No purpose data available'
                  loading={false}
                  exportable={true}
                  exportFilename='calls-by-purpose.csv'
                />
                {metrics.callsWithoutPurpose && metrics.callsWithoutPurpose > 0 && (
                  <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                    * {metrics.callsWithoutPurpose} call(s) without a defined purpose
                  </Typography>
                )}
              </Box>
            )}
        </Box>
      ) : (
        <Typography>No metrics available</Typography>
      )}
    </Box>
  );
};
